/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import { isManagerVar } from '@graphql/cache';
import { Icon } from '@mayoral/ui';
import { IconButton, Link as MuiLink, Stack, styled, Tooltip } from '@mui/material';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FC, PropsWithChildren, useEffect, useState } from 'react';

import SidebarBrand from './SidebarBrand';
import { SidebarWrapper } from './SidebarContext';
import SidebarNav from './SidebarNav';
import SidebarProfile from './SidebarProfile';

import { Routes } from '@/lib/constants';
import { PagesHeightWithEnvBanner } from '@/lib/constants/theme';

const SidebarContainerRoot = styled(Stack, {
  name: 'SidebarContainer',
  slot: 'Root',
})(({ theme }) => ({
  position: 'fixed',
  left: 0,
  height: PagesHeightWithEnvBanner,
  width: theme.spacing(30.625),
  paddingTop: theme.spacing(3),
  backgroundColor: theme.palette.common.white,
  borderRightStyle: 'solid',
  borderRightWidth: 1,
  borderColor: theme.palette.grey[200],
  overflowY: 'auto',
  zIndex: 1000,
}));

const SidebarContainerIconButton = styled(IconButton, {
  name: 'SidebarContainer',
  slot: 'IconButton',
})(({ theme }) => ({
  flexDirection: 'column',
  marginRight: theme.spacing(-1),
  width: theme.spacing(3.75),
  height: theme.spacing(3.75),
  color: theme.palette.dark[400],
  '&:hover': { color: theme.palette.secondary.main },
}));

type SideBarProps = PropsWithChildren<object>;

const SidebarContainer: FC<SideBarProps> = ({ children }) => {
  const { t } = useTranslation();
  const [isManager, setIsManager] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (isManagerVar()) {
      setIsManager(true);
    }
  }, []);

  return (
    <SidebarContainerRoot direction="column" flexGrow={1}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.5}
        sx={{ px: 2 }}
      >
        <Stack flexGrow={1} sx={{ overflow: 'hidden' }}>
          <Link href="/" passHref>
            <MuiLink underline="none">
              <SidebarBrand />
            </MuiLink>
          </Link>
        </Stack>

        {isManager && (
          <Stack>
            <Tooltip title={t('CAMBIAR_PROVEEDOR')}>
              <SidebarContainerIconButton
                color="secondary"
                onClick={() => router.push(Routes.SELECTION)}
              >
                <Icon name="ChevronUpXS" viewBox="0 0 8 8" sx={{ fontSize: 8 }} />
                <Icon name="ChevronDownXS" viewBox="0 0 8 8" sx={{ fontSize: 8 }} />
              </SidebarContainerIconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      {children}
    </SidebarContainerRoot>
  );
};

const Sidebar: FC = () => {
  return (
    <SidebarWrapper>
      <SidebarContainer>
        <Stack flexGrow={1} sx={{ my: 4 }}>
          <SidebarNav flexGrow={1} />
        </Stack>

        <SidebarProfile />
      </SidebarContainer>
    </SidebarWrapper>
  );
};

export default Sidebar;
